// src/Concept.jsx
import React from "react";

/** Local UI helpers (self-contained so no extra imports needed) */
const Button = ({ children, href = "#", variant = "primary", className = "" }) => {
  const base =
    "inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm md:text-base font-semibold shadow-sm transition-transform active:scale-[.98]";
  const variants = {
    primary: "bg-white text-gray-900 hover:bg-gray-100",
    dark: "bg-gray-900 text-white hover:bg-gray-800",
    accent: "bg-amber-500 text-gray-900 hover:bg-amber-400",
    ghost: "bg-white/10 text-white hover:bg-white/20 border border-white/20",
  };
  return (
    <a href={href} className={[base, variants[variant], className].join(" ")}>
      {children}
    </a>
  );
};

const SectionHeader = ({ eyebrow, title, subtitle, align = "left", light = false }) => (
  <header
    className={[
      "max-w-3xl",
      align === "center" ? "mx-auto text-center" : "",
      light ? "text-white" : "",
    ].join(" ")}
  >
    {eyebrow && (
      <p
        className={[
          "text-xs tracking-widest uppercase mb-2",
          light ? "text-white/80" : "text-gray-500",
        ].join(" ")}
      >
        {eyebrow}
      </p>
    )}
    <h1
      className={[
        "text-3xl md:text-5xl font-extrabold leading-tight",
        light ? "text-white" : "text-gray-900",
      ].join(" ")}
    >
      {title}
    </h1>
    {subtitle && (
      <p
        className={[
          "mt-4 text-base md:text-lg",
          light ? "text-white/80" : "text-gray-600",
        ].join(" ")}
      >
        {subtitle}
      </p>
    )}
  </header>
);

/** DEFAULT EXPORT to match: import ConceptPage from "./Concept" */
export default function ConceptPage() {
  return (
    <main className="min-h-screen bg-white text-gray-900">
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[48vh] w-full overflow-hidden">
          <img
            src="/images/blkbloc-day.jpg"
            alt="The Blk Bloc concept"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/55" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-8 h-full flex items-center">
            <div className="text-white">
              <SectionHeader
                eyebrow="Concept"
                title="The Blk Bloc • Detroit Pilot"
                subtitle="A walkable marketplace built from shipping containers—launching vendors, jobs, and culture fast."
                light
              />
              <div className="mt-6 flex flex-wrap gap-3">
                <Button href="/donate" variant="accent">Donate</Button>
                <Button href="/donate" variant="primary">See Budget</Button>
                <Button href="/" variant="ghost">Back Home</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM / SOLUTION / WHY NOW */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-8 grid md:grid-cols-3 gap-6">
          {[
            {
              t: "The Problem",
              d: "Vacant lots and shuttered storefronts drain safety and wealth from Black neighborhoods.",
            },
            {
              t: "Our Solution",
              d: "Cluster lots into a container marketplace: vendor pods, café, culture wall, youth jobs, and ownership pathways.",
            },
            {
              t: "Why Now",
              d: "Containers + community partners let us open in months, not years—then replicate across cities.",
            },
          ].map((b, i) => (
            <article
              key={i}
              className="rounded-3xl border border-gray-200 p-6 bg-white shadow-sm"
            >
              <h3 className="text-lg font-semibold">{b.t}</h3>
              <p className="mt-2 text-sm text-gray-600">{b.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* PHASE SUMMARY */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <SectionHeader
            eyebrow="Roadmap"
            title="Independent Builder Phases"
            subtitle="Lean rollout with visible wins and realistic costs."
            align="center"
          />
          <div className="mt-10 grid gap-6">
            {[
              {
                name: "Phase 1 • Ground & Trust",
                cost: "$5K–$10K",
                copy:
                  "Acquire/lease 9 lots, clean, fence, and host a Unity Cleanup Day. Meet city officials and block clubs (early allies).",
              },
              {
                name: "Phase 2 • Demolition + Pilot Pods",
                cost: "$30K–$90K",
                copy:
                  "Demo unsafe houses (partial $3–6K / full $7–12K each), grade pads, install 1–2 Basic Vendor Pods ($5–8K ea).",
              },
              {
                name: "Phase 3 • Infrastructure & Partnerships",
                cost: "$25K–$45K",
                copy:
                  "Gravel paths, lighting, shared restroom, permanent utilities, and youth/trade school build partners.",
              },
              {
                name: "Phase 4 • Expansion & Culture",
                cost: "$60K–$100K",
                copy:
                  "Add 4–6 pods, create an Ancestor Wall or Unity Garden, weekly markets and classes; begin rezoning/land-trust paperwork.",
              },
              {
                name: "Phase 5 • Legitimize & Scale",
                cost: "$10K–$20K",
                copy:
                  "Mixed-use approval, impact reporting, replication partners, and publish the Blk Bloc Playbook.",
              },
            ].map((p, i) => (
              <div
                key={i}
                className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-lg font-semibold">{p.name}</h4>
                  <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                    Budget {p.cost}
                  </span>
                </div>
                <p className="mt-3 text-sm text-gray-700">{p.copy}</p>
              </div>
            ))}
          </div>

          <p className="mt-4 text-center text-xs text-gray-500">
            * Phase 2 cost varies by demolition scope and container tier.
          </p>
        </div>
      </section>

      {/* EXPANSION VISION */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold">
            The Long-Term Vision
          </h3>
          <p className="mt-4 text-gray-700 md:text-lg">
            Not just one site—Blk Blocs in{" "}
            <span className="font-semibold text-amber-600">every major city</span>.
            A national network of <span className="font-semibold text-amber-600">200 hubs</span> that hire locally,
            train youth, and recycle profits back into the neighborhood.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button href="/donate" variant="accent">Fund Phase 2</Button>
            <Button href="/" variant="dark">Back to Home</Button>
          </div>
        </div>
      </section>
    </main>
  );
}
